import type { Command } from "../document/commands.ts";
import { doorState, type DoorState } from "../document/doors.ts";
import { sameJson } from "../document/same-json.ts";
import type { WorldDocument } from "../document/types.ts";
import { layerOf } from "./resolve-types.ts";
import { absent, fieldsBack, type Plan } from "./undo-plan.ts";

/**
 * How an undo takes back a door command (ADR-122 decision 2.1, ADR-086 decision 27): an added or removed door, and
 * a door's name, destination and fields key by key. A stroke over door cells is taken back as every cell command is
 * (`narrowedCells`).
 */

/**
 * How to take back an added or a removed door (ADR-122 decision 2.1): an added one is removed only while it is
 * still as it was added and no cell names it; a removed one is put back at its place, or last when the layer is
 * shorter now, unless another door of the map holds its id, which the document refuses (`assertUniqueDoorIds`).
 * The cells a removal cleared come back by their own stroke, taken back after the door.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
export function doorPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "addDoor" | "removeDoor" }>): Plan {
  const layer = layerOf(world, command.mapId, command.layerId);
  if (layer?.kind !== "door") {
    return absent();
  }
  const none: Plan = { tries: [], keys: 0, lost: 1 };
  const at = { mapId: command.mapId, layerId: command.layerId };
  const index = layer.doors.findIndex((candidate) => candidate.id === command.door.id);
  if (command.kind === "addDoor") {
    const held = layer.doors[index];
    if (held === undefined) {
      return absent();
    }
    return sameJson(held, command.door) && !layer.cells.includes(index + 1) ? { tries: [{ kind: "removeDoor", ...at, index, door: held }], keys: 1, lost: 0 } : none;
  }
  return { tries: [{ kind: "addDoor", ...at, index: Math.min(command.index, layer.doors.length), door: command.door }], keys: 1, lost: 0 };
}

/**
 * How to take back a change of a door: its name and its destination each as one key, and each key of its fields.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
export function doorUpdatePlan(world: WorldDocument, command: Extract<Command, { readonly kind: "updateDoor" }>): Plan {
  const layer = layerOf(world, command.mapId, command.layerId);
  const door = layer?.kind === "door" ? layer.doors.find((candidate) => candidate.id === command.doorId) : undefined;
  if (door === undefined) {
    return absent();
  }
  const { before, after } = command;
  const held = doorState(door);
  const fields = fieldsBack(door.fields, before.fields, after.fields);
  const renamed = before.name !== after.name;
  const moved = !sameJson(before.to, after.to);
  const nameBack = renamed && held.name === after.name;
  const toBack = moved && sameJson(held.to, after.to);
  const keys = fields.keys + (nameBack ? 1 : 0) + (toBack ? 1 : 0);
  const next: DoorState = { name: nameBack ? before.name : held.name, to: toBack ? before.to : held.to, fields: fields.fields };
  return { tries: keys === 0 ? [] : [{ kind: "updateDoor", mapId: command.mapId, layerId: command.layerId, doorId: command.doorId, before: held, after: next }], keys, lost: fields.lost + (renamed && !nameBack ? 1 : 0) + (moved && !toBack ? 1 : 0) };
}
